import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { SessionService } from 'src/app/services/session/session.service';
import { ThisReceiver } from '@angular/compiler';
import { ConfigurationCompanyServiceMock } from 'src/mocks/ConfigurationCompanyServiceMock';
import { WizardProductService } from 'src/app/services/product/wizard/wizard-product.service';
import { ConfigurationsEnum } from 'src/app/Enums/ConfigurationsEnum';
import { ConfigurationOptionsEnum } from 'src/app/Enums/ConfigurationOptionsEnum';
import { ConfigurationCompanyService } from 'src/app/services/configuration-company/configuration-company.service';
import { FuncionalidadeSistemaId } from 'src/app/models/User/MinhaAcaoPermissao.model';
import { PermissaoAcessoService } from 'src/app/services/permissao/permissao-acesso.service';

interface OptionsNode {
  name: string;
  id: string;
  dataTest?: string;
  children?: OptionsNode[];
  classIcon: string,
  nameIcon: string,
  url: string,
  show: boolean
}
interface ExampleFlatNode {
  expandable: boolean;
  id: string;
  dataTest?: string;
  name: string;
  level: number;
  classIcon: string,
  nameIcon: string,
  url: string,
  show: boolean
}
@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss']
})

export class SideMenuComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('sidenavRef', { read: ElementRef }) sidenavRef!: ElementRef<HTMLElement>;
  private removeHoverListeners: (() => void)[] = [];

  get urlCurrentPage(): string {
    return this.router.url;
  }

  get nameUser(): string {
    return this.sessionService.getDetails().fullName
  }
  get emailUser(): string {
    return this.sessionService.getDetails().email
  }

  private _showNfceMenu: boolean = false;
  get showNfceMenu(): boolean {
    return this._showNfceMenu;
  }

  private _showUserListMenu: boolean = false;
  get showUserListMenu(): boolean {
    return this._showUserListMenu;
  }

  private readonly TREE_DATA: OptionsNode[] = [
    {
      id: 'homeButton',
      dataTest: 'menu-home',
      name: this.translateService.instant("sideMenu.sectionsName.myAccount.items.item1"),
      classIcon: 'material-icons',
      nameIcon: 'dashboard',
      url: 'home',
      show: true
    },
    {
      id: 'productsButton',
      dataTest: 'menu-produtos',
      name: this.translateService.instant("sideMenu.sectionsName.products.name"),
      classIcon: 'material-icons',
      nameIcon: 'storefront',
      url: 'product',
      show: true
    },
    {
      id: 'nfceButton',
      dataTest: 'menu-nfce',
      name: 'NFC-e',
      classIcon: 'material-icons',
      nameIcon: 'receipt_long',
      url: 'nfce',
      show: this.showNfceMenu
    },
    {
      id: 'pdvButton',
      dataTest: 'menu-pdv',
      name: this.translateService.instant("sideMenu.sectionsName.pdv.name"),
      classIcon: 'material-icons',
      nameIcon: "phone_android",
      url: 'pdv',
      show: true
    },
    {
      id: 'sales',
      dataTest: 'menu-vendas',
      name: this.translateService.instant("sideMenu.sectionsName.sales.name"),
      children: [
        {
          id: 'salesButton',
          dataTest: 'relatorio-vendas',
          name: this.translateService.instant("sideMenu.sectionsName.sales.options.report"),
          classIcon: 'material-icons-outlined',
          nameIcon: 'analytics',
          url: 'sales/report',
          show: true
        },
        {
          id: 'consumptionTicketSalesButton',
          dataTest: 'relatorio-ficha-consumo',
          name: this.translateService.instant("sideMenu.sectionsName.sales.options.consumptionTicket"),
          classIcon: 'material-icons-outlined',
          nameIcon: 'confirmation_number',
          url: 'sales/consumption-ticket',
          show: true
        },
        {
          id: 'cashboxButton',
          dataTest: 'relatorio-caixa',
          name: this.translateService.instant("sideMenu.sectionsName.sales.options.cashbox"),
          classIcon: 'material-icons',
          nameIcon: 'point_of_sale',
          url: 'sales/cashbox',
          show: true
        }
      ],
      classIcon: 'material-icons-outlined',
      nameIcon: 'calculate',
      url: 'sales',
      show: true
    },
    {
      id: 'userListButton',
      dataTest: 'menu-lista-usuarios',
      name: this.translateService.instant("sideMenu.sectionsName.userList.name"),
      classIcon: 'material-icons',
      nameIcon: 'people',
      url: 'user',
      show: this.showUserListMenu
    },
    {
      id: 'suportButton',
      dataTest: 'menu-suporte',
      name: this.translateService.instant("sideMenu.sectionsName.support.name"),
      classIcon: 'material-icons',
      nameIcon: 'headset_mic',
      url: 'support',
      show: true
    },
    {
      id: 'logoutButton',
      dataTest: 'menu-logout',
      name: this.translateService.instant("sideMenu.sectionsName.exit.name"),
      classIcon: 'material-icons',
      nameIcon: 'logout',
      url: '',
      show: true
    },

  ];
  // Item do menu (id) -> funcionalidade que controla o item. Novos itens com permissão entram aqui.
  private readonly FUNCIONALIDADE_POR_ITEM: Record<string, number> = {
    homeButton: FuncionalidadeSistemaId.AcessarDashboard,
    productsButton: FuncionalidadeSistemaId.AcessarProduto,
    nfceButton: FuncionalidadeSistemaId.AcessarNfce,
    pdvButton: FuncionalidadeSistemaId.AcessarPdv,
    salesButton: FuncionalidadeSistemaId.AcessarRelatorioVendas,
    cashboxButton: FuncionalidadeSistemaId.AcessarRelatorioCaixa,
    consumptionTicketSalesButton: FuncionalidadeSistemaId.AcessarFichaConsumo,
    userListButton: FuncionalidadeSistemaId.AcessarUsuarios
  };

  // Itens ocultos pela ação 3 (Bloquear e ocultar). Fica separado do "show" porque outras regras
  // (ex.: NFC-e ativa) também ligam e desligam o item; o item só aparece se as duas permitirem.
  private ocultosPorPermissao = new Set<string>();

  // As telas só aparecem depois de carregadas as permissões; se falhar, o painel mostra o erro
  permissoesProntas: boolean = false;
  falhaAoCarregarPermissoes$ = this.permissaoAcesso.falhaAoCarregar$;

  private _transformer = (node: OptionsNode, level: number): ExampleFlatNode => {
    return {
      expandable: !!node.children && node.children.length > 0,
      id: node.id,
      dataTest: node.dataTest,
      name: node.name,
      level: level,
      classIcon: node.classIcon,
      nameIcon: node.nameIcon,
      url: node.url,
      show: node.show
    };
  }
  collapse: boolean = true;
  treeControl = new FlatTreeControl<ExampleFlatNode>(node => node.level, node => node.expandable);
  treeFlattener = new MatTreeFlattener(this._transformer, node => node.level, node => node.expandable, node => node.children);
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  deviceDesktop: boolean = false;
  sidebarOpened: boolean = false;
  sidebarFixed: boolean = false;
  mouseHovering: boolean = false;

  constructor(private router: Router,
    private translateService: TranslateService,
    private authenticationSerivce: AuthenticationService,
    private sessionService: SessionService,
    private configurationService: ConfigurationCompanyService,
    private wizardProduct: WizardProductService,
    private permissaoAcesso: PermissaoAcessoService,
    private ngZone: NgZone,
    private renderer: Renderer2
  ) {
    this.dataSource.data = this.TREE_DATA;
  }

  private visivel(node: ExampleFlatNode): boolean {
    return node.show && !this.ocultosPorPermissao.has(node.id);
  }

  hasChild = (_: number, node: ExampleFlatNode) => { return node.expandable && this.visivel(node) };
  showNode = (_: number, node: ExampleFlatNode) => { return this.visivel(node) && !node.expandable };

  // Carrega as permissões no início da sessão. Sem conseguir, o painel não mostra telas protegidas (falha fechada)
  private async carregarPermissoesDoMenu(): Promise<void> {
    this.permissoesProntas = false;
    this.ocultosPorPermissao.clear();

    const carregou = await this.permissaoAcesso.carregarPermissoes(true);
    if (!carregou) return;

    // Consulta todas as funcionalidades do menu ao mesmo tempo
    await Promise.all(Object.entries(this.FUNCIONALIDADE_POR_ITEM).map(async ([itemId, funcionalidadeId]) => {
      if (await this.permissaoAcesso.estaOculta(funcionalidadeId)) this.ocultosPorPermissao.add(itemId);
    }));

    // Grupo com filhos (ex.: Vendas) some quando todos os filhos controlados por permissão estão ocultos.
    // Filho sem funcionalidade (ex.: Ficha de Consumo) não segura o grupo aberto.
    this.TREE_DATA
      .filter(item => {
        const controlados = item.children?.filter(filho => filho.id in this.FUNCIONALIDADE_POR_ITEM) ?? [];
        return controlados.length > 0 && controlados.every(filho => this.ocultosPorPermissao.has(filho.id));
      })
      .forEach(grupo => this.ocultosPorPermissao.add(grupo.id));

    this.forceTreeUpdate();
    this.permissoesProntas = true;
  }

  async tentarCarregarPermissoesNovamente(): Promise<void> {
    await this.carregarPermissoesDoMenu();
  }

  private checkDevice() {
    const screen_size_desktop = window.matchMedia("(min-width: 600px)");
    const getScreenSizeChange = () => {
      this.deviceDesktop = screen_size_desktop.matches
    }
    getScreenSizeChange();

    window.addEventListener('resize', getScreenSizeChange);
  }

  async ngOnInit(): Promise<void> {
    this.checkDevice();
    this.carregarPermissoesDoMenu();
    this.showOrHideTreeNode('userListButton', true);
    this.wizardProduct.configurationSet().then(configurations => {
      configurations.forEach(x => {
        if (x.configurationId == ConfigurationsEnum.EmitNfce)
          this.showOrHideTreeNode('nfceButton', x.configurationOptionId == ConfigurationOptionsEnum.NfceYes);
      })
    });
    this.configurationService.getEmitNfce().subscribe((r) => {
      this.showOrHideTreeNode('nfceButton', r);
    });
  }

  // Hover fora da zona do Angular para evitar change detection global a cada mouseenter/mouseleave.
  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      const element = this.sidenavRef.nativeElement;
      const onEnter = () => this.sidebarHover(true);
      const onLeave = () => this.sidebarHover(false);
      this.removeHoverListeners.push(
        this.renderer.listen(element, 'mouseenter', onEnter),
        this.renderer.listen(element, 'mouseleave', onLeave)
      );
    });
  }

  ngOnDestroy(): void {
    this.removeHoverListeners.forEach(remove => remove());
  }

  showSidebar() {
    this.sidebarOpened = true;
  }

  hideSidebar() {
    this.sidebarOpened = false;
  }

  toggleSidebar() {
    if (this.sidebarOpened) {
      this.hideSidebar();
    } else {
      this.showSidebar();
    }
  }

  sidebarHover(hover: boolean) {
    if (this.sidebarFixed) {
      return;
    }

    if (hover) {
      if (this.sidebarOpened) {
        this.mouseHovering = true;
        return;
      }

      this.ngZone.run(() => {
        this.treeControl.collapseAll();
        this.showSidebar();
        this.mouseHovering = true;
      });
    } else {
      this.mouseHovering = false;
      setTimeout(() => {
        if (this.sidebarFixed == true || this.mouseHovering) {
          return;
        }
        this.ngZone.run(() => {
          this.treeControl.collapseAll();
          this.hideSidebar();
        });
      }, 500);
    }
  }

  clickMenuButton() {
    if (this.sidebarOpened && this.sidebarFixed == true) {
      this.hideSidebar();
      setTimeout(() => { this.sidebarFixed = false; }, 10);
    } else {
      this.showSidebar();
      this.sidebarFixed = true;
    }
  }

  logout() {
    this.authenticationSerivce.logout();
  }

  showOrHideTreeNode(nodeId: string, show: boolean) {
    this.dataSource.data.map((n) => {
      if (n.id == nodeId)
        n.show = show;
      return n;
    });
    this.forceTreeUpdate();
  }

  forceTreeUpdate() {
    const data = this.dataSource.data;
    this.dataSource.data = [];
    this.dataSource.data = data;
  }
}